import { getRecognitionNormColor, NORM_COLOR_CLASS, NORM_COLOR_HEX } from 'src/app/common/constants/norms-color.constants';

/**
 * When multiple trendline series share the same category and value, Highcharts
 * paints later series on top so only the last colour/label is visible.
 * These helpers collapse those coincident points into one pie-style marker
 * that shows every series colour, and keep a single data label.
 */

export function applyOverlappingSameValueMarkers(seriesData: any[]): void {
  if (!Array.isArray(seriesData) || seriesData.length < 2) {
    return;
  }

  const categoryCount = Math.max(
    ...seriesData.map((series) => series?.data?.length || 0),
    0
  );

  for (let categoryIndex = 0; categoryIndex < categoryCount; categoryIndex++) {
    const buckets = new Map<string, { series: any; point: any }[]>();

    for (const series of seriesData) {
      const point = series?.data?.[categoryIndex];
      if (!isPlottableOverlapPoint(point)) {
        continue;
      }
      const key = String(point.y);
      const bucket = buckets.get(key) || [];
      bucket.push({ series, point });
      buckets.set(key, bucket);
    }

    buckets.forEach((group) => {
      if (group.length < 2) {
        return;
      }

      const seriesColors = group.map(
        (item) => item.series.color || item.point.color || '#999999'
      );
      const normColors = group.map((item) => item.point.normColor || null);
      const names = group.map((item) => item.series.name);
      const symbol = overlappingMarkerSymbol(seriesColors);

      group.forEach((item, index) => {
        item.point.overlapSeriesNames = names;
        item.point.overlapSeriesColors = seriesColors;
        item.point.overlapNormColors = normColors;
        if (index === group.length - 1) {
          item.point.marker = {
            enabled: true,
            symbol,
            width: 12,
            height: 12,
            radius: 6
          };
        } else {
          item.point.marker = { enabled: false };
          item.point.dataLabels = { enabled: false };
        }
      });
    });
  }

}

export function overlapLegendItemClickHandler(event?: any, series?: any): void {
  const legendItem = series || event?.legendItem || event?.target;
  onOverlapLegendItemClick(legendItem);
}

export function onOverlapLegendItemClick(clickedSeries: any): void {
  if (!clickedSeries?.chart?.series) {
    return;
  }
  const grouped =
    seriesOption(clickedSeries, '_overlapGroupId') ||
    seriesOption(clickedSeries, '_combinedLegendName');
  if (!grouped) {
    return;
  }
  // Highcharts 11 toggles the clicked series first; match the rest after that.
  setTimeout(() => syncOverlapGroupVisibility(clickedSeries), 0);
}

function seriesOption(series: any, key: string): any {
  return series?.[key] ?? series?.userOptions?.[key] ?? series?.options?.[key];
}

function syncOverlapGroupVisibility(clickedSeries: any): void {
  const chartSeries = clickedSeries?.chart?.series;
  if (!Array.isArray(chartSeries)) {
    return;
  }

  const targetVisible = clickedSeries.visible !== false;
  let didChange = false;
  chartSeries.forEach((series: any) => {
    if (series === clickedSeries || !isSameOverlapLegendGroup(series, clickedSeries)) {
      return;
    }
    if (series.visible !== targetVisible) {
      series.setVisible(targetVisible, false);
      didChange = true;
    }
  });
  if (didChange) {
    clickedSeries.chart?.redraw();
  }
}

function isSameOverlapLegendGroup(series: any, clickedSeries: any): boolean {
  const clickedGroup =
    seriesOption(clickedSeries, '_overlapGroupId') ||
    seriesOption(clickedSeries, '_combinedLegendName');
  const seriesGroup =
    seriesOption(series, '_overlapGroupId') ||
    seriesOption(series, '_combinedLegendName');
  if (clickedGroup && seriesGroup && clickedGroup === seriesGroup) {
    return true;
  }

  const clickedName = String(clickedSeries?.name || seriesOption(clickedSeries, 'name') || '');
  const seriesName = String(series?.name || seriesOption(series, 'name') || '');
  if (clickedName && seriesName && clickedName === seriesName && clickedName.includes(',')) {
    return true;
  }

  if (clickedName.includes(',')) {
    const original = String(
      seriesOption(series, '_originalLegendName') || seriesName
    ).trim();
    const members = clickedName.split(',').map((part: string) => part.trim());
    if (original && members.includes(original)) {
      return true;
    }
  }

  const clickedOverlap = collectOverlapMemberNames(clickedSeries);
  const seriesOriginal = String(
    seriesOption(series, '_originalLegendName') || seriesName
  ).trim();
  if (
    seriesOption(series, 'showInLegend') === false &&
    seriesOriginal &&
    clickedOverlap.has(seriesOriginal)
  ) {
    return true;
  }

  return false;
}

function collectOverlapMemberNames(series: any): Set<string> {
  const names = new Set<string>();
  const original = seriesOption(series, '_originalLegendName');
  if (original) {
    names.add(String(original).trim());
  }
  const points = series?.points || series?.userOptions?.data || series?.options?.data || [];
  points.forEach((point: any) => {
    (point?.overlapSeriesNames || []).forEach((name: string) => {
      if (name) {
        names.add(String(name).trim());
      }
    });
  });
  return names;
}

function getVisibleOverlapInfo(point: any): {
  names: string[];
  colors: string[];
  norms: (string | null | undefined)[];
  hadOverlap: boolean;
} {
  const names = (point?.overlapSeriesNames as string[] | undefined) || [];
  const colors = (point?.overlapSeriesColors as string[] | undefined) || [];
  const norms = (point?.overlapNormColors as (string | null | undefined)[] | undefined) || [];
  const hadOverlap = names.length > 1 || colors.length > 1 || norms.length > 1;
  const visibleNames: string[] = [];
  const visibleColors: string[] = [];
  const visibleNorms: (string | null | undefined)[] = [];

  const slotCount = Math.max(names.length, colors.length, norms.length);
  for (let index = 0; index < slotCount; index++) {
    const name = names[index];
    if (name && !isOverlapSeriesVisible(point, name)) {
      continue;
    }
    if (!name && point?.series?.visible === false) {
      continue;
    }
    if (name) {
      visibleNames.push(name);
    }
    if (index < colors.length) {
      visibleColors.push(colors[index]);
    }
    if (index < norms.length) {
      visibleNorms.push(norms[index]);
    }
  }

  return { names: visibleNames, colors: visibleColors, norms: visibleNorms, hadOverlap };
}

function seriesMatchesOverlapName(series: any, overlapName: string): boolean {
  const original =
    series?.userOptions?._originalLegendName ?? series?._originalLegendName;
  return (
    original === overlapName ||
    series?.name === overlapName ||
    series?.userOptions?.name === overlapName
  );
}

function isOverlapSeriesVisible(point: any, overlapName: string): boolean {
  const chartSeries = point?.series?.chart?.series;
  if (!Array.isArray(chartSeries) || !overlapName) {
    return point?.series?.visible !== false;
  }
  const matches = chartSeries.filter((series: any) =>
    seriesMatchesOverlapName(series, overlapName)
  );
  if (matches.length === 0) {
    return point?.series?.visible !== false;
  }
  return matches.some((series: any) => series.visible !== false);
}

export function formatOverlapNormValue(
  value: string,
  point: any,
  options?: { collapseByColor?: boolean }
): string {
  if (value == null || value === 'I/S' || value === 'NA') {
    return `${value ?? ''}`;
  }
  const rawValue = stripOverlapValue(String(value));
  const visible = getVisibleOverlapInfo(point);
  if (visible.hadOverlap && visible.names.length === 0 && visible.norms.length === 0) {
    return '';
  }
  const overlapNorms = visible.hadOverlap
    ? visible.norms
    : (point?.overlapNormColors as (string | null | undefined)[] | undefined);
  const overlapNames = visible.hadOverlap
    ? visible.names
    : (point?.overlapSeriesNames as string[] | undefined);
  const uniqueNames = uniquePreserveOrder((overlapNames || []).filter(Boolean));
  const slotCount = uniqueNames.length > 1
    ? uniqueNames.length
    : Math.max(overlapNorms?.length || 0, overlapNames?.length || 0);
  // Recognition/share: never colour the raw value. Median/recognition colour belongs on the change.
  const colorValue = !point?.isPercentage;
  if (slotCount > 1) {
    const colors = Array.from({ length: slotCount }, (_, index) => overlapNorms?.[index] || point?.normColor || '');
    const hasNormColor = colorValue && (colors.some(Boolean) || !!point?.normColor);
    // Without norms, list one value per overlapping metric (4 metrics → 4 values).
    const colorsToPaint = hasNormColor
      ? (options?.collapseByColor ? uniquePreserveOrder(colors.filter(Boolean)) : colors)
      : Array.from({ length: slotCount }, () => '');
    if (colorsToPaint.length === 0) {
      return rawValue;
    }
    return colorsToPaint
      .map((color) =>
        color ? `<span style="color:${color};">${rawValue}</span>` : `<span>${rawValue}</span>`
      )
      .join('<span>, </span>');
  }
  if (colorValue && point?.normColor) {
    return `<span style="color:${point.normColor};">${rawValue}</span>`;
  }
  return `${rawValue}`;
}

export function resolveTrendChangeColor(point: any, fallbackColor = 'black'): string {
  if (point?.changeNormColor) {
    return point.changeNormColor;
  }
  if (point?.isPercentage) {
    const sig = point?.Significance_Value != null ? Number(point.Significance_Value) : null;
    const colorName = getRecognitionNormColor(
      Number.isFinite(sig as number) ? sig : null,
      point?.Sig_LowerLimt,
      point?.Sig_UpperLimt,
      point?.cov
    );
    return NORM_COLOR_HEX[colorName];
  }
  return fallbackColor;
}

export function formatTrendChangeSpan(changeText: string, point: any, fallbackColor = 'black'): string {
  const sig = point?.Significance_Value != null ? Number(point.Significance_Value) : null;
  const colorName = point?.isPercentage
    ? getRecognitionNormColor(
        Number.isFinite(sig as number) ? sig : null,
        point?.Sig_LowerLimt,
        point?.Sig_UpperLimt,
        point?.cov
      )
    : null;
  const color = point?.changeNormColor || (colorName ? NORM_COLOR_HEX[colorName] : fallbackColor);
  const cls = colorName ? NORM_COLOR_CLASS[colorName] : '';
  return `<span class="${cls}" style="font-size: 80%; color: ${color};">(${changeText})</span>`;
}

function stripOverlapValue(value: string): string {
  const withoutTags = String(value ?? '').replace(/<[^>]+>/g, '');
  return withoutTags.split(',')[0].trim();
}

function uniquePreserveOrder(colors: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const color of colors) {
    const key = color.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(color);
    }
  }
  return unique;
}

export function overlapSeriesNamesText(point: any, fallbackName: string): string {
  const visible = getVisibleOverlapInfo(point);
  if (visible.names.length > 1) {
    return uniquePreserveOrder(visible.names).join(', ');
  }
  if (visible.names.length === 1) {
    return visible.names[0];
  }
  if (visible.hadOverlap) {
    return '';
  }
  const legendText = point?.overlapLegendText as string | undefined;
  if (legendText) {
    return legendText;
  }
  const combined =
    point?.series?.userOptions?._combinedLegendName ||
    point?.series?._combinedLegendName ||
    point?.series?.name;
  if (combined && String(combined).includes(',')) {
    return String(combined);
  }
  return fallbackName;
}

export function overlapSeriesDotsHtml(
  point: any,
  fallbackName: string,
  fallbackColor: string
): string {
  const visible = getVisibleOverlapInfo(point);
  const names = visible.hadOverlap
    ? visible.names
    : (point?.overlapSeriesNames as string[] | undefined);
  const colors = visible.hadOverlap
    ? visible.colors
    : (point?.overlapSeriesColors as string[] | undefined);
  if (names && colors && names.length > 1) {
    const seen = new Set<string>();
    return names
      .map((name, index) => {
        if (!name || seen.has(name.toLowerCase())) {
          return '';
        }
        seen.add(name.toLowerCase());
        const color = colors[index] || fallbackColor;
        return (
          `<div style="display:flex;align-items:center;margin-bottom:2px;"><div style="width: 1em; height: 1em; background-color: ${color}; border-radius: 50%;"></div>` +
          `<div style="padding-left:5%;">${name}</div></div>`
        );
      })
      .join('');
  }

  return (
    `<div style="display:flex"><div style="width: 1em; height: 1em; background-color: ${fallbackColor}; border-radius: 50%;"> </div>` +
    `<div style="width:90%; padding-left:5%;"> ${fallbackName} </div></div>`
  );
}

export function overlapLegendColorDotHtml(series: any, fallbackColor: string, size = 9): string {
  const color = fallbackColor || series?.legendkeycolor || series?.color || '#999999';
  return (
    `<div style="flex-shrink:0;width:${size}px;height:${size}px;background-color:${color};border-radius:50%;"></div>`
  );
}

export function overlappingMarkerSymbol(colors: string[], size = 14): string {
  const radius = size / 2;
  const sliceCount = Math.max(colors.length, 1);
  const slices = colors
    .map((color, index) => {
      const start = (index / sliceCount) * 2 * Math.PI - Math.PI / 2;
      const end = ((index + 1) / sliceCount) * 2 * Math.PI - Math.PI / 2;
      const x0 = radius + radius * Math.cos(start);
      const y0 = radius + radius * Math.sin(start);
      const x1 = radius + radius * Math.cos(end);
      const y1 = radius + radius * Math.sin(end);
      const largeArc = end - start > Math.PI ? 1 : 0;
      return `<path d="M${radius},${radius} L${x0.toFixed(3)},${y0.toFixed(3)} A${radius},${radius} 0 ${largeArc} 1 ${x1.toFixed(3)},${y1.toFixed(3)} Z" fill="${color}"/>`;
    })
    .join('');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${slices}</svg>`;
  return `url(data:image/svg+xml,${encodeURIComponent(svg)})`;
}

function isPlottableOverlapPoint(point: any): boolean {
  if (!point || point.y == null || Number.isNaN(Number(point.y))) {
    return false;
  }
  if (point.cov === 'I/S') {
    return false;
  }
  if (point.y === 0 && (point.y1 == null || point.y1 === 0) && point.samplesize != null) {
    return false;
  }
  return true;
}
